import { cn } from '@/lib/cn'
import { type VariantProps, cva } from 'class-variance-authority'
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'

const alertVariants = cva(
  'relative flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-sm',
  {
    variants: {
      variant: {
        info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200',
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200',
        warning:
          'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200',
        danger:
          'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200',
      },
    },
    defaultVariants: { variant: 'info' },
  },
)

const iconByVariant = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
} as const

interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: ReactNode
  icon?: boolean
}

export function Alert({
  className,
  variant = 'info',
  title,
  icon = true,
  children,
  ...props
}: AlertProps) {
  const Icon = iconByVariant[variant ?? 'info']
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> : null}
      <div className="flex min-w-0 flex-col gap-0.5">
        {title ? <p className="font-semibold leading-tight">{title}</p> : null}
        {children ? <div className="leading-snug">{children}</div> : null}
      </div>
    </div>
  )
}
